'use client';

import { c as _c } from "react/compiler-runtime";
import { type ReactNode, useEffect, useMemo, useRef } from 'react';
import { LoadingMorph } from '@extern/ui/loading-morph';
import dynamic from 'next/dynamic';
import { usePathname, useSearchParams } from 'next/navigation';
import { useAuthEvents } from '@/app/(auth)/_hooks/use-auth-events';
import { parseInterviewArrival } from '@/app/(auth)/sign-up/_utils/interview-arrival';
import { parseLabsArrival } from '@/app/(auth)/sign-up/_utils/labs-arrival';
import { parsePortfolioArrival } from '@/app/(auth)/sign-up/_utils/portfolio-arrival';
import { useSignUpQ1Variant } from '../_hooks/use-sign-up-q1-variant';
import { AuthLayout } from './auth-layout';
import { CenteredAuthLayout } from './centered-auth-layout';

/**
 * Loaded only when the URL says the visitor just claimed a portfolio, so the panel and its lookup
 * never reach the bundle of an ordinary sign-up or sign-in load.
 */
const PortfolioArrivalLayout = dynamic(() => import('./portfolio-arrival/portfolio-arrival-layout').then(mod => mod.PortfolioArrivalLayout), {
  loading: () => <AuthLoading />,
  ssr: false
});

/**
 * The Labs arrival layout is loaded only when a URL actually carries a lab result, so the packs it
 * renders from never reach the bundle of an ordinary sign-up or sign-in load.
 */
const LabsArrivalLayout = dynamic(() => import('./labs-arrival/labs-arrival-layout').then(mod => mod.LabsArrivalLayout), {
  loading: () => <AuthLoading />,
  ssr: false
});

/** The practice-interview arrival, loaded only when a URL carries a scorecard, for the same reason. */
const InterviewArrivalLayout = dynamic(() => import('./interview-arrival/interview-arrival-layout').then(mod => mod.InterviewArrivalLayout), {
  loading: () => <AuthLoading />,
  ssr: false
});
function AuthLoading() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="flex min-h-screen w-full flex-col items-center justify-center bg-role-surface"><LoadingMorph /></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
const CONTROL_LAYOUT_TEXT = <>
    Gain Real Experience.
    <br />
    Build Essential Skills.
    <br />
    Drive Your Career Forward.
  </>;

/**
 * What `auth_screen_viewed` carries for the two arrivals that get their own layout. The two are told
 * apart by campaign tags that never co-occur; the portfolio one is checked first to match the render.
 */
function arrivalEventProps(portfolioArrival: ReturnType<typeof parsePortfolioArrival>, labsArrival: ReturnType<typeof parseLabsArrival>, interviewArrival: ReturnType<typeof parseInterviewArrival>): Record<string, unknown> | undefined {
  if (portfolioArrival) {
    return {
      portfolio_arm: portfolioArrival.arm,
      portfolio_handoff: true
    };
  }
  if (labsArrival) {
    return {
      labs_archetype: labsArrival.archetypeId,
      labs_rail: true
    };
  }
  if (interviewArrival) {
    return {
      interview_rail: true,
      interview_rounds: interviewArrival.levels.join('.')
    };
  }
  return;
}
interface AuthLayoutSwitcherProps {
  children: ReactNode;
}
export function AuthLayoutSwitcher(t0) {
  const $ = _c(32);
  const {
    children
  } = t0;
  const {
    isReady,
    variant
  } = useSignUpQ1Variant();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const {
    trackAuthScreenViewed
  } = useAuthEvents();
  const lastTrackedPathRef = useRef(null);
  let t1;
  if ($[0] !== pathname || $[1] !== searchParams) {
    t1 = pathname?.includes("/sign-up") ? parsePortfolioArrival(searchParams) : null;
    $[0] = pathname;
    $[1] = searchParams;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const portfolioArrival = t1;
  let t2;
  if ($[3] !== pathname || $[4] !== searchParams) {
    t2 = pathname?.includes("/sign-up") ? parseLabsArrival(searchParams) : null;
    $[3] = pathname;
    $[4] = searchParams;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const labsArrival = t2;
  let t3;
  if ($[6] !== pathname || $[7] !== searchParams) {
    t3 = pathname?.includes("/sign-up") ? parseInterviewArrival(searchParams) : null;
    $[6] = pathname;
    $[7] = searchParams;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const interviewArrival = t3;
  let t4;
  let t5;
  if ($[9] !== interviewArrival || $[10] !== isReady || $[11] !== labsArrival || $[12] !== pathname || $[13] !== portfolioArrival || $[14] !== trackAuthScreenViewed) {
    t4 = () => {
      if (!(isReady && pathname)) {
        return;
      }
      if (lastTrackedPathRef.current === pathname) {
        return;
      }
      if (pathname.includes("/sign-up")) {
        trackAuthScreenViewed("sign_up", "entry", "start", arrivalEventProps(portfolioArrival, labsArrival, interviewArrival));
        lastTrackedPathRef.current = pathname;
        return;
      }
      if (pathname.includes("/sign-in")) {
        trackAuthScreenViewed("sign_in", "entry", "start");
        lastTrackedPathRef.current = pathname;
      }
    };
    t5 = [interviewArrival, isReady, labsArrival, pathname, portfolioArrival, trackAuthScreenViewed];
    $[9] = interviewArrival;
    $[10] = isReady;
    $[11] = labsArrival;
    $[12] = pathname;
    $[13] = portfolioArrival;
    $[14] = trackAuthScreenViewed;
    $[15] = t4;
    $[16] = t5;
  } else {
    t4 = $[15];
    t5 = $[16];
  }
  useEffect(t4, t5);
  if (!isReady) {
    let t6;
    if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <AuthLoading />;
      $[17] = t6;
    } else {
      t6 = $[17];
    }
    return t6;
  }
  if (portfolioArrival) {
    let t6;
    if ($[18] !== children || $[19] !== portfolioArrival || $[20] !== variant) {
      t6 = <PortfolioArrivalLayout arrival={portfolioArrival} variant={variant}>{children}</PortfolioArrivalLayout>;
      $[18] = children;
      $[19] = portfolioArrival;
      $[20] = variant;
      $[21] = t6;
    } else {
      t6 = $[21];
    }
    return t6;
  }
  let t6;
  if ($[22] !== children || $[23] !== variant) {
    t6 = variant === "experiment" ? <CenteredAuthLayout>{children}</CenteredAuthLayout> : <AuthLayout text={CONTROL_LAYOUT_TEXT}>{children}</AuthLayout>;
    $[22] = children;
    $[23] = variant;
    $[24] = t6;
  } else {
    t6 = $[24];
  }
  const shipped = t6;
  if (labsArrival) {
    let t7;
    if ($[25] !== children || $[26] !== labsArrival || $[27] !== shipped) {
      t7 = <LabsArrivalLayout fallback={shipped} params={labsArrival}>{children}</LabsArrivalLayout>;
      $[25] = children;
      $[26] = labsArrival;
      $[27] = shipped;
      $[28] = t7;
    } else {
      t7 = $[28];
    }
    return t7;
  }
  if (interviewArrival) {
    let t7;
    if ($[29] !== children || $[30] !== interviewArrival) {
      t7 = <InterviewArrivalLayout params={interviewArrival}>{children}</InterviewArrivalLayout>;
      $[29] = children;
      $[30] = interviewArrival;
      $[31] = t7;
    } else {
      t7 = $[31];
    }
    return t7;
  }
  return shipped;
}